import DatePicker from "components/DatePicker/DatePicker";
import { useEffect, useState } from "react";

// STYLES
import {
  AgendaContainer,
  BackIcon,
  BodyContainer,
  BodyContent,
  HeaderContainer,
  TopSection,
  TopSectionHeader,
} from "./AgendaStyled";

// LIBRARIES

import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

//ASETS
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";

// MISC
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";

// REDUX

import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import AgendaRow from "components/AgendaRow/AgendaRow";
import CustomText from "components/Atoms/CustomText/CustomText";
import newsAndMediaService from "services/newsAndMediaService";

const Agenda = () => {
  // PROPERTIES

  // API REQUESTS

  // LIBRARY CONSTANTS
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // STATE CONSTANTS
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [agenda, setAgenda] = useState<any[]>([]);
  // LIFE CYCLE

  // EVENT HANDLERS
  const formatDate = (day, month, year) => {
    const formattedDay = day.toString().padStart(2, "0");
    const formattedMonth = (month + 1).toString().padStart(2, "0");
    return `${formattedDay}.${formattedMonth}.${year}`;
  };

  const getDayOfWeek = (year, month, day) => {
    const date = new Date(year, month, day);
    const dayIndex = date.getDay();
    const days = [
      t("view.newsAndMedia.agendaPage.sunday"),
      t("view.newsAndMedia.agendaPage.monday"),
      t("view.newsAndMedia.agendaPage.tuesday"),
      t("view.newsAndMedia.agendaPage.wednesday"),
      t("view.newsAndMedia.agendaPage.thursday"),
      t("view.newsAndMedia.agendaPage.friday"),
      t("view.newsAndMedia.agendaPage.saturday"),
    ];
    return days[dayIndex];
  };

  const isWeekend = (year: number, month: number, day: number) => {
    const dow = new Date(year, month, day).getDay();
    return dow === 0 || dow === 6; // Sun(0) / Sat(6)
  };

  // past working day (Mon–Fri) strictly before today
  const isPastWorkingDay = (year: number, month: number, day: number) => {
    const d = new Date(year, month, day);
    const today = new Date();
    // normalize to midnight for both
    d.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const dow = d.getDay();
    const isWeekday = dow >= 1 && dow <= 5;
    return isWeekday && d.getTime() < today.getTime();
  };

  useEffect(() => {
    newsAndMediaService
      .getAgendas(selectedYear, selectedMonth + 1, selectedDay)
      .then((response) => {
        setAgenda(response?.data?.[0]?.attributes?.content ?? []);
      })
      .catch((err) => {
        console.log("getAgendas error", err);
        setAgenda([]); // fail-safe
      });
  }, [selectedYear, selectedMonth, selectedDay]);

  const noAgendaWeekendRow = (
    <HeaderContainer>
      <CustomText
        text={t("app.freeDay") || "Program liber"}
        type={TextType.P1}
        color={COLORS.MetalicSilver}
      />
    </HeaderContainer>
  );

  const pastWorkingDayRow = (
    <AgendaRow
      key="past-working-day"
      title=""
      description={t("app.currentActivity") || "Activitate curenta"}
      color={COLORS.VividAuburn}
      isLast
    />
  );


  return (
    <AgendaContainer>
      <TopSection>
        <TopSectionHeader
          onClick={() => navigate(-1)}
          biggerCursor={biggerCursor}
        >
          <BackIcon src={LeftArrow} alt="Back" />
          <CustomText
            userSelect={false}
            type={TextType.H5boldspaced}
            text={t("view.newsAndMedia.officialAnnouncements.backButton")}
            color={COLORS.RaisinBlack}
          />
        </TopSectionHeader>
        <CustomText
          type={TextType.H2}
          text={t("view.newsAndMedia.agenda")}
          color={COLORS.RaisinBlack}
        />
      </TopSection>
      <DatePicker
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        selectedDay={selectedDay}
        setSelectedDay={setSelectedDay}
      />
         <BodyContainer>
        <HeaderContainer>
          <CustomText
            type={TextType.H3}
            text={getDayOfWeek(selectedYear, selectedMonth, selectedDay)}
            color={COLORS.RaisinBlack}
          />
          <CustomText
            type={TextType.P1}
            text={formatDate(selectedDay, selectedMonth, selectedYear)}
            color={COLORS.MetalicSilver}
          />
        </HeaderContainer>

        <BodyContent>
          {agenda && agenda.length > 0 ? (
            agenda.map((item, index) => (
              <AgendaRow
                key={index}
                title={item?.title || ""}
                description={item?.text ? item.text : (t("app.freeDay") || "Program liber")}
                color={COLORS.VividAuburn}
                isLast={index === agenda.length - 1}
              />
            ))
          ) : isWeekend(selectedYear, selectedMonth, selectedDay) ? (
            // Weekends: "Program liber"
            noAgendaWeekendRow
          ) : isPastWorkingDay(selectedYear, selectedMonth, selectedDay) ? (
            // Past working days with no agenda: "Activitate curenta"
            pastWorkingDayRow
          ) : (
            // Future/today weekdays with no items: keep generic message
            <HeaderContainer>
              <CustomText
                text={t("app.noSchedule")}
                type={TextType.P1}
                color={COLORS.MetalicSilver}
              />
            </HeaderContainer>
          )}
        </BodyContent>
      </BodyContainer>
    </AgendaContainer>
  );
};

export default Agenda;